import { useState, FC, forwardRef } from 'react';

import { useTheme } from 'styled-components';
import { Popover } from 'react-tiny-popover';

import CustomIcon from '@components/utils/CustomIcon';
import { LocationAnimationErrorBox } from '@components/views/components/listing/ListingStyle';
import ErrorMessage from '@components/ErrorMessage';
import { DownArrowIcon, UpArrowIcon } from '@assets/icons';
import useMountedRef from '@hooks/useMountedRef';

import {
  StyledSearchBarWrapper,
  SearchBarLabel,
  InputContentWrapper,
  IconRightSpace,
  SearchBarInputText,
  RenderOverlayContentWrapper,
  SearchBarInputWrapper,
} from './SearchBarInputStyle';
import {
  SearchBarInputProps,
  SearchBarItemProps,
} from './SearchBarInputInterface';

export const SearchBarItem: FC<SearchBarItemProps> = ({
  leftIcon,
  rightIcon,
  isActive,
  label,
  InputComponent,
  isFilled,
  isFlight,
}) => (
  <>
    {leftIcon ? (
      <>
        <CustomIcon fontSize={28} icon={leftIcon} />
        <IconRightSpace />
      </>
    ) : null}

    <InputContentWrapper>
      <SearchBarLabel isActive={isActive} isFilled={isFilled}>
        {label}
      </SearchBarLabel>
      {InputComponent ? (
        <SearchBarInputText isActive={isActive} isFilled={isFilled}>
          {InputComponent}
        </SearchBarInputText>
      ) : null}
    </InputContentWrapper>
    {label === 'Nereye Gideceksiniz?' ? (
      <CustomIcon icon={rightIcon} />
    ) : (
      <CustomIcon
        paletteColor='blackAlpha'
        icon={isFlight ? null : isActive ? UpArrowIcon : DownArrowIcon}
      />
    )}
  </>
);

export type CustomPopoverChildComponentProps =
  React.ComponentPropsWithoutRef<'div'>;

const CustomPopoverChildComponent = forwardRef<
  HTMLDivElement,
  CustomPopoverChildComponentProps
>((props, ref) => (
  <StyledSearchBarWrapper ref={ref} onClick={props.onClick}>
    {props.children}
  </StyledSearchBarWrapper>
));

const SearchBarInput: FC<SearchBarInputProps> = ({
  label,
  leftIcon,
  rightIcon,
  InputComponent,
  renderOverlayContent,
  error = false,
  errorMessage = '',
  hasAnimationOnError,
  isFilled,
  tourStyle,
  tourListing,
  isFlight,
  popoverContentReady,
  popoverProps: {
    //defaultIsOpen and isOpen should not be set together.
    isOpen, //controlled popover
    defaultIsOpen, //un-controlled popover
    padding = 10,
    onVisibleChange,
    onClickOutside,
    containerStyle,
    ...otherPopoverProps
  } = {},
}) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState<boolean>(!!defaultIsOpen);
  const theme = useTheme();

  const { parentRef, isParentReady } = useMountedRef<HTMLDivElement>();
  const isControlledPopover = typeof isOpen === 'boolean';

  const closeOverlay = () => {
    isControlledPopover ? onVisibleChange?.(false) : setIsOverlayOpen(false);
  };

  const toggleOverlay = () => {
    isControlledPopover
      ? setTimeout(() => onVisibleChange?.(!isOpen))
      : setTimeout(() => setIsOverlayOpen(!isOverlayOpen));
  };

  const isOverlayVisible = isControlledPopover ? !!isOpen : isOverlayOpen;
  const isReadinessEnabled = typeof popoverContentReady === 'boolean';
  const isPopoverContentVisible =
    !isReadinessEnabled || (isOverlayVisible && popoverContentReady);
  const defaultContainerStyle: Partial<CSSStyleDeclaration> = {
    zIndex: `${theme.zIndices.searchBar}`,
    backgroundColor: 'white',
    width: tourListing ? '818px' : tourStyle ? '1098px' : '540px',
    display: 'flex',
    justifyContent: 'center',
    margin: '0px',
    boxShadow: '0 20px 40px 0 rgba(0, 0, 0, 0.2)',
  };
  // Merge caller overrides onto the default host presentation. Calendar
  // searchbars override only boxShadow; width/background/z-index/layout remain.
  const inheritedContainerStyle = {
    ...defaultContainerStyle,
    ...containerStyle,
  };
  const resolvedContainerStyle = isReadinessEnabled
    ? {
        ...inheritedContainerStyle,
        opacity: isPopoverContentVisible ? '1' : '0',
        pointerEvents: isPopoverContentVisible ? 'auto' : 'none',
      }
    : inheritedContainerStyle;

  return (
    <div ref={parentRef}>
      {isParentReady && parentRef?.current ? (
        <Popover
          positions={['bottom', 'left']}
          align='start'
          padding={padding}
          containerStyle={resolvedContainerStyle}
          reposition={false}
          parentElement={parentRef?.current as HTMLElement}
          content={() => {
            return renderOverlayContent ? (
              <RenderOverlayContentWrapper
                aria-hidden={
                  isReadinessEnabled ? !isPopoverContentVisible : undefined
                }
                inert={
                  isReadinessEnabled && !isPopoverContentVisible
                    ? true
                    : undefined
                }
              >
                {renderOverlayContent(closeOverlay)}
              </RenderOverlayContentWrapper>
            ) : (
              <div /> //TODO: it will never render, ts null error fix
            );
          }}
          isOpen={isOverlayVisible}
          onClickOutside={(e) => {
            onClickOutside?.(e);
            closeOverlay();
          }}
          {...otherPopoverProps}
        >
          <CustomPopoverChildComponent onClick={toggleOverlay}>
            <LocationAnimationErrorBox
              hasError={error}
              hasAnimationOnError={hasAnimationOnError}
            >
              <SearchBarInputWrapper>
                <SearchBarItem
                  isFlight={isFlight}
                  label={label}
                  leftIcon={leftIcon}
                  rightIcon={rightIcon}
                  InputComponent={InputComponent}
                  isActive={isOverlayVisible}
                  isFilled={isFilled}
                />
              </SearchBarInputWrapper>
            </LocationAnimationErrorBox>
            {error && errorMessage ? (
              <ErrorMessage message={errorMessage} showIcon={false} />
            ) : null}
          </CustomPopoverChildComponent>
        </Popover>
      ) : null}
    </div>
  );
};

export default SearchBarInput;
